import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TopBar from '../global/TopBar';
import { IoClose, IoSearch, IoPersonAdd, IoHome, IoPeople, IoBusinessSharp, IoAdd, IoEllipsisVertical, IoTime, IoCheckmark, IoClose as IoCloseIcon, IoChevronForward } from 'react-icons/io5';
import { FaUserFriends, FaUsers, FaBriefcase, FaUserPlus, FaRegStar, FaUserTie, FaCheck } from 'react-icons/fa';
import { MdAdd, MdFamilyRestroom, MdPeople, MdMoreHoriz } from 'react-icons/md';
import debounce from 'lodash/debounce';
import { useSelector, useDispatch } from 'react-redux';
import store from '../../state';
import userService from '../../services/userService';
import {
  fetchUserConnections,
  fetchPendingConnections,
  sendConnectionRequest,
  sendBatchConnectionRequests,
  acceptConnection,
  rejectConnection,
  removeConnection,
  searchUsers,
  resetStatus,
  addRealTimeRequest
} from '../../state/connectionSlice';
import setupSockets, { 
  onConnectionRequest, 
  onConnectionAccepted, 
  onConnectionRejected, 
  onConnectionRemoved,
  offConnectionRequest,
  offConnectionAccepted,
  offConnectionRejected,
  offConnectionRemoved
} from '../../socket/socketSetup';
import * as connectionService from '../../services/connectionService';

import ConnectionCard from './components/ConnectionCard';
import ConnectionSearch from './components/ConnectionSearch';
import LoadingSpinner from '../../components/LoadingSpinner';
import { addConnectionRequestNotification, addConnectionAcceptedNotification } from '../../state/notificationSlice';
import PendingRequestsDialog from './components/PendingRequestsDialog';

// Custom toast function using feedback state
const useToast = () => {
  const [feedbackMessages, setFeedbackMessages] = useState([]);
  
  // Function to show toast message
  const showToast = ({ title, description, status, duration = 3000 }) => {
    const id = Date.now();
    const message = { id, title, description, status };
    
    setFeedbackMessages(prev => [...prev, message]);
    
    // Auto-remove after duration
    setTimeout(() => {
      setFeedbackMessages(prev => prev.filter(msg => msg.id !== id));
    }, duration);
  };
  
  return {
    showToast,
    feedbackMessages
  };
};

// Connection List Component
const ConnectionList = ({ connections, onRemove }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3"
    >
      {connections.map(connection => (
        <ConnectionCard
          key={connection.id || connection._id || connection.connectionId || `connection-${Date.now()}-${Math.random()}`}
          connection={connection}
          onRemove={onRemove}
        />
      ))}
    </motion.div>
  );
};

// Add Connection Modal Component
const AddConnectionModal = ({ isOpen, onClose, group }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const connectionsList = useSelector((state) => state.connections.connections);
  const pendingRequests = useSelector((state) => state.connections.pendingConnections) || { incoming: [], outgoing: [] };
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [selectedConnectionType, setSelectedConnectionType] = useState('friend');
  const toast = useToast();

  // Clear search results and selected users when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedUsers([]);
      // Clear search results when modal closes
      dispatch(searchUsers({ query: '', userId: user.id }));
    }
  }, [isOpen, dispatch, user.id]);

  const handleSendRequests = async () => {
    if (selectedUsers.length === 0) {
      toast.showToast({
        title: 'No Users Selected',
        description: 'Please select at least one user',
        status: 'warning'
      });
      return;
    }
    
    try {
      setIsProcessing(true);
      
      // Use batch connection request for multiple users
      if (selectedUsers.length > 1) {
        await dispatch(sendBatchConnectionRequests({
          requesterId: user.id,
          recipientIds: selectedUsers,
          requestType: group || selectedConnectionType,
          message: requestMessage
        }));
      } else {
        // For a single user, use the regular connection request
        await dispatch(sendConnectionRequest({
          requesterId: user.id,
          recipientId: selectedUsers[0],
          requestType: group || selectedConnectionType,
          message: requestMessage
        }));
      }
      
      // Show success message
      toast.showToast({
        title: 'Requests Sent',
        description: `Sent ${selectedUsers.length} connection ${selectedUsers.length === 1 ? 'request' : 'requests'} successfully`,
        status: 'success'
      });
      
      // Refresh pending connections
      dispatch(fetchPendingConnections({ userId: user.id, direction: 'outgoing' }));
      
      // Close modal
      onClose();
    } catch (error) {
      // Show error message
      toast.showToast({
        title: 'Error',
        description: error.message || 'Failed to send connection requests',
        status: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Function to handle selecting a user
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSearch = async (term) => {
    try {
      if (!term.trim()) return [];
      
      // Dispatch the searchUsers action with the current user ID
      await dispatch(searchUsers({
        query: term,
        userId: user.id
      }));
      
      // Get the filtered results from the Redux store
      const searchResults = store.getState().connections.searchResults;
      
      // Filter out users that are already connected or have pending requests
      const filteredResults = searchResults.filter(searchUser => {
        if (!searchUser || !searchUser.id) {
          console.warn("AddConnectionModal: Invalid search result without ID:", searchUser);
          return false;
        }
        
        // Check if this user is already in any of our connections
        const isAlreadyConnected = Object.values(connectionsList || {}).some(connectionGroup => {
          return (Array.isArray(connectionGroup) && connectionGroup.some(conn => {
            if (!conn) return false;
            const connectedId = conn.connectedUserId || 
                               (conn.user && conn.user.id) || 
                               conn.userId;
            return connectedId === searchUser.id;
          }));
        });
        
        // Check if this user is in pending requests
        const isPendingOutgoing = Array.isArray(pendingRequests.outgoing) && 
          pendingRequests.outgoing.some(req => {
            if (!req) return false;
            const recipientId = req.recipientId || (req.recipient && req.recipient.id);
            return recipientId === searchUser.id;
          });
        
        const isPendingIncoming = Array.isArray(pendingRequests.incoming) && 
          pendingRequests.incoming.some(req => {
            if (!req) return false;
            const requesterId = req.requesterId || (req.requester && req.requester.id);
            return requesterId === searchUser.id;
          });
        
        // Only include users that are not already connected and not in pending requests
        const shouldInclude = !isAlreadyConnected && !isPendingOutgoing && !isPendingIncoming;
        
        return shouldInclude;
      });
      
      return filteredResults;
      
    } catch (error) {
      console.error("AddConnectionModal: Error searching for users:", error);
      return [];
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-pri-dark/80 backdrop-blur-md" onClick={onClose} />
          
          {/* Modal */}
          <motion.div
            initial={{ y: 20, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            exit={{ y: 20, opacity: 0 }}
            className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
          >
            {/* Glossy Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-sec-dark to-pri-dark">
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-mint-green/5" />
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent" />
                <div className="absolute -left-24 -top-24 w-48 h-48 bg-soft-pink/20 rounded-full filter blur-[80px] opacity-30" />
                <div className="absolute -right-24 -bottom-24 w-48 h-48 bg-mint-green/20 rounded-full filter blur-[80px] opacity-20" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>
            </div>
            
            {/* Content */}
            <div className="relative p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent">
                  Add {group} Connection
                </h2>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 rounded-full bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 border border-soft-pink/20 hover:border-soft-pink/40 transition-all"
                >
                  <IoClose className="h-5 w-5 text-pri-white" />
                </motion.button>
              </div>

              <div className="mb-6">
                <ConnectionSearch
                  onSearch={handleSearch}
                  onSelectUser={handleSelectUser}
                  selectedUsers={selectedUsers}
                  pendingRequests={pendingRequests}
                  connections={[
                    ...connectionsList.family || [],
                    ...connectionsList.friends || [],
                    ...connectionsList.business || []
                  ]}
                />
                
                {selectedUsers.length > 0 && (
                  <div className="mt-4 p-3 rounded-xl bg-gradient-to-br from-pri-dark/40 to-sec-dark/40 border border-soft-pink/10">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-pri-white">{selectedUsers.length} users selected</span>
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setSelectedUsers([])}
                        className="text-xs text-pri-gray hover:text-pri-white px-2 py-1 rounded-lg bg-pri-dark/40"
                      >
                        Clear all
                      </motion.button>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose} 
                  className="px-4 py-2 rounded-lg bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 border border-soft-pink/20 text-pri-white"
                  disabled={isProcessing}
                >
                  Cancel
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSendRequests}
                  disabled={selectedUsers.length === 0 || isProcessing}
                  className={`px-4 py-2 rounded-lg transition-all ${
                    selectedUsers.length === 0 || isProcessing
                      ? 'bg-gradient-to-br from-soft-pink/30 to-mint-green/30 text-pri-white/70 cursor-not-allowed'
                      : 'bg-gradient-to-br from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg'
                  }`}
                >
                  {isProcessing ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-t-transparent border-pri-dark rounded-full animate-spin mr-2"></div>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    `Send ${selectedUsers.length > 0 ? selectedUsers.length : ''} Request${selectedUsers.length !== 1 ? 's' : ''}`
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Redesigned TabButton component to match Calendar styling
const TabButton = ({ active, onClick, icon, label, count, smallScreen }) => (
  <motion.button
    whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className={`relative flex items-center justify-center px-4 py-2 ${
      smallScreen ? 'rounded-full w-10 h-10' : 'rounded-xl'
    } transition-all duration-300 ${
      active 
        ? 'bg-gradient-to-br from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg'
        : 'bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 text-pri-white border border-soft-pink/20 hover:from-soft-pink/15 hover:to-mint-green/10'
    }`}
  >
    <div className="flex items-center">
      <span className="text-xl">{icon}</span>
      {!smallScreen && <span className="ml-2">{label}</span>}
    </div>
    {count > 0 && (
      <span className="absolute -top-2 -right-2 flex items-center justify-center h-5 w-5 text-xs font-bold rounded-full bg-soft-pink text-pri-dark shadow-lg z-30">
        {count}
      </span>
    )}
  </motion.button>
);

// Redesigned AddConnectionButton to match Calendar styling
const AddConnectionButton = ({ onClick }) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    className="flex items-center justify-center rounded-full w-10 h-10 bg-gradient-to-br from-soft-pink to-mint-green text-pri-dark shadow-lg"
    aria-label="Add connection"
  >
    <IoAdd className="text-xl" />
  </motion.button>
);

// Redesigned EmptyState component to match Calendar styling
const EmptyState = ({ type, onAdd }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col items-center justify-center p-8 rounded-2xl bg-gradient-to-br from-pri-dark/80 to-sec-dark/80 backdrop-blur-xl border border-soft-pink/10 shadow-xl text-center my-8"
  >
    <div className="p-4 rounded-full bg-gradient-to-br from-soft-pink/20 to-mint-green/20 mb-4">
      {type === 'family' ? (
        <MdFamilyRestroom className="text-5xl text-soft-pink" />
      ) : type === 'friends' ? (
        <FaUserFriends className="text-5xl text-mint-green" />
      ) : (
        <FaUserTie className="text-5xl text-pri-yellow" />
      )}
    </div>
    <h3 className="text-xl font-bold text-pri-white mb-2">No {type} connections yet</h3>
    <p className="text-pri-gray mb-6">
      Start adding {type} members to your network
    </p>
    <motion.button
      whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
      whileTap={{ scale: 0.95 }}
      onClick={() => onAdd(type)}
      className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-br from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg"
    >
      <IoPersonAdd className="text-xl" />
      <span>Add {type}</span>
    </motion.button>
  </motion.div>
);

// Main Component
const ConnectDashboard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const connectionsState = useSelector((state) => state.connections);
  
  // Provide defaults for when state is not yet initialized
  const connections = connectionsState?.connections || { family: [], friends: [], professional: [] };
  const pendingConnections = connectionsState?.pendingConnections || { incoming: [], outgoing: [] };
  const pendingRequests = connectionsState?.pendingRequests || pendingConnections || { incoming: [], outgoing: [] };
  const status = connectionsState?.status || 'idle';
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPendingRequestsOpen, setIsPendingRequestsOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('family');
  const [feedback, setFeedback] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [activeTab, setActiveTab] = useState('family'); // 'family', 'friends', or 'others'
  const [processingRequestId, setProcessingRequestId] = useState(null);
  const toast = useToast();

  // Fetch connections and pending requests on mount
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchUserConnections({ userId: user.id }))
        .then(result => {
          console.log('Connections fetch result:', result);
        })
        .catch(err => {
          console.error('Error fetching connections:', err);
        });
      
      // Fetch pending connections only when the Connect page mounts
      dispatch(fetchPendingConnections({ userId: user.id, direction: 'incoming' }));
      dispatch(fetchPendingConnections({ userId: user.id, direction: 'outgoing' }));
    }
  }, [dispatch, user?.id]);

  // Initialize socket connection
  useEffect(() => {
    if (user?.id) {
      console.log('Initializing socket connection for user:', user.id);
      
      // Socket connections are now handled at the app level through App.jsx
      // So we don't need to explicitly initialize them here
      
      // Cleanup function
      return () => {
        console.log('Socket connections are managed at the app level');
        // No need to close sockets here as they are managed globally
      };
    }
  }, [user?.id]);

  // Function to analyze the structure of a request object for debugging
  const analyzeRequestStructure = (request) => {
    console.log('Request structure:', {
      id: request.id || request._id,
      requesterId: request.requesterId,
      recipientId: request.recipientId,
      requesterName: request.requesterName || (request.requester && request.requester.name),
      recipientName: request.recipientName || (request.recipient && request.recipient.name),
      status: request.status,
      type: request.type || request.requestType,
      createdAt: request.createdAt,
      updatedAt: request.updatedAt
    });
  };

  // Function to handle connection request updates
  const handleNewRequest = (request) => {
    // Analyze the structure of the request to ensure proper mapping
    
    try {
      // Add the request to the pending requests list
      dispatch(addRealTimeRequest(request));
      
      // Fetch updated pending requests
      dispatch(fetchPendingConnections({ userId: user.id, direction: 'incoming' }));
      
      // Show feedback
      setFeedback('New connection request received');
      setTimeout(() => setFeedback(''), 3000);
    } catch (error) {
      // Handle error silently
    }
  };
  
  // Function to handle connection accepted/rejected
  const handleRequestUpdate = (data) => {
    // Refresh connections and pending requests
    dispatch(fetchUserConnections({ userId: user.id }));
    dispatch(fetchPendingConnections({ userId: user.id, direction: 'outgoing' }));
  };
  
  // Function to handle connection removed
  const handleConnectionRemoved = (connectionId) => {
    // Refresh connections and pending requests
    dispatch(fetchUserConnections({ userId: user.id }));
    dispatch(fetchPendingConnections({ userId: user.id, direction: 'incoming' }));
    dispatch(fetchPendingConnections({ userId: user.id, direction: 'outgoing' }));
  };

  // Function to handle connection status updates
  const handleConnectionStatusUpdate = (event) => {
    // Refresh connection data only
    dispatch(fetchUserConnections({ userId: user.id }));
  };

  // useEffect for setting up event listeners after all handler functions are defined
  useEffect(() => {
    // Register socket event listeners
    onConnectionRequest(handleNewRequest);
    onConnectionAccepted(handleRequestUpdate);
    onConnectionRejected(handleRequestUpdate);
    onConnectionRemoved(handleConnectionRemoved);
    
    // Set up status update event listener
    window.addEventListener('connection-status-update', handleConnectionStatusUpdate);
    
    return () => {
      // Clean up socket event listeners
      offConnectionRequest(handleNewRequest);
      offConnectionAccepted(handleRequestUpdate);
      offConnectionRejected(handleRequestUpdate);
      offConnectionRemoved(handleConnectionRemoved);
      
      // Clean up DOM event listener
      window.removeEventListener('connection-status-update', handleConnectionStatusUpdate);
    };
  }, [dispatch, user.id]);

  // In the Connect component, add useEffect to listen for pending requests changes
  useEffect(() => {
    // When we have incoming requests and the notification is not yet shown
    if (pendingRequests?.incoming?.length > 0 && !showNotification) {
      setShowNotification(true);
    } else if (pendingRequests?.incoming?.length === 0 && showNotification) {
      // When we have no more incoming requests, hide the notification
      setShowNotification(false);
    }
  }, [pendingRequests, showNotification]);

  // Update hasPendingRequests helper method to include safeguards
  const hasPendingRequests = pendingRequests?.incoming?.length > 0 || false;

  const handleAddConnection = (group) => {
    setSelectedGroup(group || activeTab);
    setIsAddModalOpen(true);
  };

  const handleRemoveConnection = async (connection) => {
    if (!connection) {
      setFeedback('Failed to remove connection: Invalid connection');
      setTimeout(() => setFeedback(''), 3000);
      return;
    }
    
    // Show loading feedback
    setFeedback('Removing connection...');
    
    try {
      // Extract the connection ID from the connection object
      let connectionId = connection.connectionId;  // Try connectionId first
      
      // If no connectionId, try to construct it from the connected user's ID
      if (!connectionId) {
        // Get the connected user's ID
        const connectedUserId = connection.id || 
                               connection._id || 
                               connection.connectedUserId || 
                               connection.userId || 
                               (connection.user && connection.user.id) ||
                               (connection.connectedUser && connection.connectedUser.id);
                               
        if (!connectedUserId) {
          throw new Error("Could not find connected user ID");
        }
        
        // Construct the connection ID using both user IDs
        // The connection ID should be in the format: smaller_user_id:larger_user_id
        const ids = [user.id, connectedUserId].sort();
        connectionId = `${ids[0]}:${ids[1]}`;
      }
      
      if (!connectionId) {
        throw new Error("Could not determine connection ID");
      }
      
      // Dispatch the removeConnection action with the correct connection ID
      const result = await dispatch(removeConnection({
        connectionId,
        userId: user.id
      })).unwrap();
      
      // Show success feedback
      setFeedback('Connection removed successfully');
      setTimeout(() => setFeedback(''), 3000);
      
      // Refresh connections
      dispatch(fetchUserConnections({ userId: user.id }));
    } catch (error) {
      setFeedback(`Failed to remove connection: ${error.message || 'Unknown error'}`);
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  // Update handleRequestResponse to safely access pendingRequests
  const handleRequestResponse = async (requestId, accept, requestType) => {
    try {
      setProcessingRequestId(requestId);
      
      // Normalize requestId (it might be passed as an object or with different property names)
      let normalizedRequestId;
      
      if (typeof requestId === 'object') {
        // Extract ID from request object
        normalizedRequestId = requestId.id || requestId._id || requestId.requestId;
      } else {
        normalizedRequestId = requestId;
      }
      
      // Find the request in pending connections to log the full request
      const request = [...(pendingRequests.incoming || []), ...(pendingRequests.outgoing || [])]
        .find(req => req.id === normalizedRequestId || req._id === normalizedRequestId || req.requestId === normalizedRequestId);
      
      if (accept) {
        await dispatch(acceptConnection({ 
          requestId: normalizedRequestId,
          userId: user.id
        }));
        
        // Refetch connections to include the newly accepted one
        dispatch(fetchUserConnections({ userId: user.id }));
      } else {
        await dispatch(rejectConnection({ 
          requestId: normalizedRequestId,
          userId: user.id 
        }));
      }
      
      // Refresh incoming requests after action
      dispatch(fetchPendingConnections({ userId: user.id, direction: 'incoming' }));
    } catch (error) {
      // Show error message via toast
      toast.showToast({
        title: 'Error',
        description: `Failed to ${accept ? 'accept' : 'reject'} connection request: ${error.message}`,
        status: 'error'
      });
    } finally {
      setProcessingRequestId(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return <IoTime className="text-pri-yellow" />;
      case 'ACCEPTED':
        return <IoCheckmark className="text-green-500" />;
      case 'REJECTED':
        return <IoClose className="text-red-500" />;
      default:
        return <IoTime className="text-pri-gray" />;
    }
  };

  const filterConnections = (type) => {
    if (type === 'all') {
      return [
        ...(connections.family || []),
        ...(connections.friends || []),
        ...(connections.others || [])
      ];
    }
    return connections[type] || [];
  };

  // Map business connections to "others" category
  const mapConnectionsToNewCategories = () => {
    // Ensure all connection arrays exist
    const family = Array.isArray(connections.family) ? connections.family : [];
    const friends = Array.isArray(connections.friends) ? connections.friends : [];
    const business = Array.isArray(connections.business) ? connections.business : [];
    const professional = Array.isArray(connections.professional) ? connections.professional : [];
    
    return {
      family: family,
      friends: friends,
      others: [...business, ...professional] // Map business and professional to others
    };
  };

  // Get the mapped connections
  const mappedConnections = mapConnectionsToNewCategories();

  // Get connection counts for each category
  const connectionCounts = {
    family: mappedConnections.family.length,
    friends: mappedConnections.friends.length,
    others: mappedConnections.others.length,
    total: mappedConnections.family.length + mappedConnections.friends.length + mappedConnections.others.length
  };

  // Debug function to log pending connections
  const logPendingConnections = () => {
    const state = store.getState();
    const pendingConnections = state.connections?.pendingConnections;
  };

  // Add this useEffect after the existing ones
  useEffect(() => {
    if (user && user.id) {
      // Explicitly fetch both incoming and outgoing pending connections
      dispatch(fetchPendingConnections({ userId: user.id, direction: 'incoming' }));
      dispatch(fetchPendingConnections({ userId: user.id, direction: 'outgoing' }));
      
      // Also fetch user connections
      dispatch(fetchUserConnections({ userId: user.id }));
      
      // Check current state after 2 seconds to give time for the API to respond
      setTimeout(() => {
        logPendingConnections();
      }, 2000);
    }
  }, [dispatch, user]);

  // Add URL parameter handling in the component
  useEffect(() => {
    // Check URL parameters when component mounts
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    const showDialog = params.get('showDialog');
    
    // If view=incoming is set, activate the incoming tab
    if (view === 'incoming') {
      setActiveTab('incoming');
    }
    
    // If showDialog=true is set, open the pending connections dialog
    if (showDialog === 'true') {
      setIsPendingRequestsOpen(true);
    }
  }, []);

  if (status.connections === 'loading') {
    return (
      <div className="flex justify-center items-center h-screen bg-pri-dark">
        <LoadingSpinner size="lg" color="yellow" />
      </div>
    );
  }

  // Calculate total pending requests
  const totalPendingRequests = pendingRequests.incoming.length + pendingRequests.outgoing.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pri-dark via-[#1a1c2e] to-sec-dark text-pri-white">
      {/* Top Navigation Bar */}
      <TopBar />

      {/* Header */}
      <div className="relative bg-gradient-to-b from-pri-dark/90 via-sec-dark/90 to-pri-dark/90 shadow-2xl">
        {/* Glossy Overlay with soft-pink accents */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-mint-green/5" />
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
          <div className="absolute -left-24 -top-24 w-48 h-48 bg-soft-pink/20 rounded-full filter blur-[80px] opacity-30" />
          <div className="absolute -right-24 -bottom-24 w-48 h-48 bg-mint-green/20 rounded-full filter blur-[80px] opacity-20" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <motion.h1 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl font-bold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent"
              >
                Connections
              </motion.h1>
              
              <div className="flex items-center gap-2">
                <AddConnectionButton onClick={() => handleAddConnection(activeTab)} />
                
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsPendingRequestsOpen(true)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-gradient-to-br from-soft-pink/15 to-mint-green/10 text-pri-white border border-soft-pink/20 hover:from-soft-pink/25 hover:to-mint-green/20 transition-all duration-300 shadow-lg"
                >
                  <span className="flex items-center justify-center">
                    <FaUserFriends className="text-soft-pink" />
                  </span>
                  <span className="text-sm font-medium hidden sm:inline">
                    Pending Requests
                  </span>
                  {pendingRequests.incoming.length > 0 && (
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-pri-yellow text-pri-dark text-xs font-bold">
                      {pendingRequests.incoming.length}
                    </div>
                  )}
                </motion.button>
              </div>
            </div>
            
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-pri-gray mb-6"
            >
              Manage your family, friends and other connections
            </motion.p>
            
            {/* Connection Type Tabs */}
            <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide p-2">
              <TabButton
                active={activeTab === 'family'}
                onClick={() => setActiveTab('family')}
                icon={<MdFamilyRestroom />}
                label="Family"
                count={mappedConnections.family?.length || 0}
                smallScreen={false}
              />
              <TabButton
                active={activeTab === 'friends'}
                onClick={() => setActiveTab('friends')}
                icon={<FaUserFriends />}
                label="Friends"
                count={mappedConnections.friends?.length || 0}
                smallScreen={false}
              />
              <TabButton
                active={activeTab === 'others'}
                onClick={() => setActiveTab('others')}
                icon={<FaUserTie />}
                label="Others"
                count={mappedConnections.others?.length || 0}
                smallScreen={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Status Messages */}
        <AnimatePresence>
          {feedback && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-4 p-4 rounded-xl bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark border border-soft-pink/20"
            >
              <p>{feedback}</p>
            </motion.div>
          )}
          
          {/* Feedback Message Toast */}
          {toast.feedbackMessages.map(message => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mb-4 p-4 rounded-xl ${
                message.status === 'success' 
                  ? 'bg-gradient-to-r from-mint-green/20 to-mint-green/10 text-mint-green border border-mint-green/20' 
                  : message.status === 'warning'
                  ? 'bg-gradient-to-r from-pri-yellow/20 to-pri-yellow/10 text-pri-yellow border border-pri-yellow/20'
                  : 'bg-gradient-to-r from-red-500/20 to-red-500/10 text-red-500 border border-red-500/20'
              }`}
            >
              <div className="flex items-center">
                {message.status === 'success' && <FaCheck className="h-4 w-4 mr-2" />}
                <div>
                  <p className="font-medium">{message.title}</p>
                  {message.description && <p className="text-sm">{message.description}</p>}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Connections List or Empty State */}
        <div className="mt-4">
          {status.connections === 'loading' ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 border-2 border-t-soft-pink border-soft-pink/30 rounded-full animate-spin"></div>
                <p className="mt-4 text-pri-gray">Loading connections...</p>
              </div>
            </div>
          ) : mappedConnections[activeTab].length > 0 ? (
            <ConnectionList 
              connections={mappedConnections[activeTab]}
              onRemove={handleRemoveConnection}
            />
          ) : (
            <EmptyState 
              type={activeTab === 'family' ? 'family' : activeTab === 'friends' ? 'friends' : 'others'} 
              onAdd={handleAddConnection} 
            />
          )}
        </div>
      </div>

      {/* Add Connection Modal */}
      <AddConnectionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        group={selectedGroup}
      />

      {/* Pending Requests Dialog */}
      <PendingRequestsDialog
        isOpen={isPendingRequestsOpen}
        onClose={() => setIsPendingRequestsOpen(false)}
        pendingRequests={pendingRequests.incoming}
        onAccept={(requestId) => handleRequestResponse(requestId, true)}
        onReject={(requestId) => handleRequestResponse(requestId, false)}
        processingRequestId={processingRequestId}
      />
    </div>
  );
};

export default ConnectDashboard; 